import type { MerchantUser } from 'api/merchantUsers'
import { rolesApi } from 'api/roles'
import { isAdminRole } from 'constants/roles'
import { useCallback } from 'react'
import { useAbility } from 'utilities/ability'

export type AdminPredicate = {
  isAdmin: (user: Pick<MerchantUser, 'rbac_role_ids'>) => boolean
  // True ONLY while the roles query is genuinely in flight. Never true for a
  // skipped or errored query.
  isLoading: boolean
  // The predicate could not be resolved — the query was skipped (no `members: view`)
  // or it failed. isAdmin then matches NOBODY, so every RBAC admin silently demotes
  // out of the ADMINS group. Callers must surface this rather than render as if
  // nothing happened; the ADMIN badge is the only signal separating the groups.
  isUnavailable: boolean
}

// Answers "which of THESE users are admins?" for a merchant_users list.
//
// Distinct from useIsRbacAdmin, which answers "am *I* an admin?" and fetches that
// one user — using it per row would be one request per user. This resolves the admin
// role id once and tests it against each row's rbac_role_ids, which the list payload
// already carries.
const useAdminPredicate = ({
  skip = false,
}: { skip?: boolean } = {}): AdminPredicate => {
  const ability = useAbility()
  // GET /roles authorizes on rbac_authorize!('view', 'members') (nexus
  // .../api/v1/roles.rb:9), which permissionMap maps to `access Users`. Skipping
  // keeps a 403 — rendered as a red danger toast — off the screen for users who
  // legitimately can't read the roles list.
  //
  // `skip` is for a caller that has its OWN reason not to need the predicate yet.
  // Without it, a caller that skip-gates its user-list fetch still fires this one,
  // so the roles GET goes out for a list nobody is going to render — which is how
  // the activation summary ended up issuing a request for a modal an admin never
  // opens. Folded into canFetchRoles so isUnavailable stays honest: skipped means
  // "could not be resolved", which is exactly what the flag already documents.
  const canFetchRoles = !skip && ability?.can('access', 'Users')

  const {
    data: rolesData,
    isLoading: rolesLoading,
    isError: rolesError,
  } = rolesApi.useGetRolesQuery(undefined, { skip: !canFetchRoles })

  const adminRoleId = rolesData?.roles.find(isAdminRole)?.id ?? null

  const isAdmin = useCallback(
    (user: Pick<MerchantUser, 'rbac_role_ids'>) =>
      adminRoleId !== null && (user.rbac_role_ids ?? []).includes(adminRoleId),
    [adminRoleId]
  )

  // A skipped query is "cannot determine", not "in flight"; a terminal error
  // resolves rather than hanging a caller's spinner.
  const isLoading =
    !!canFetchRoles && !rolesError && (rolesLoading || !rolesData)

  const isUnavailable = !canFetchRoles || !!rolesError

  return { isAdmin, isLoading, isUnavailable }
}

export default useAdminPredicate
